import { type BootstrapIconName } from "@/types/bootstrap-icons";

export type ConfirmDialogOptions = {
  title: string;
  text: string;
  confirmText: string;
  cancelText?: string;
  confirmKind?: "primary" | "secondary" | "danger";
  confirmIcon?: BootstrapIconName;
};

export type AlertDialogOptions = {
  title: string;
  text: string;
  confirmText: string;
};

export type QueuedDialog = ConfirmDialogOptions & {
  id: number;
  showCancelButton: boolean;
  resolve: (confirmed: boolean) => void;
};

const queue = ref<QueuedDialog[]>([]);
let mountedHosts = 0;
let nextId = 0;

const enqueue = (
  request: Omit<QueuedDialog, "id" | "resolve">
): Promise<boolean> => {
  if (mountedHosts === 0) {
    // A pending promise nobody can settle looks like a dead button, so fail
    // loudly and treat it as a cancel instead.
    console.error(
      "[confirmDialog] No ConfirmDialogHost is mounted — resolving as cancelled.",
      request
    );

    return Promise.resolve(false);
  }

  return new Promise<boolean>((resolve) => {
    queue.value = [...queue.value, { ...request, id: nextId++, resolve }];
  });
};

/**
 * Ask the user to confirm, without owning any modal state.
 *
 * Prefer `<BaseConfirmModal :open>` when the component has a template and the
 * confirm button needs a pending state; this helper is for plain confirms and
 * for callers that cannot render one — composables and plain `.ts` modules.
 */
export const openConfirmDialog = (
  options: ConfirmDialogOptions
): Promise<boolean> => enqueue({ ...options, showCancelButton: true });

/**
 * Tell the user something they can only acknowledge. Resolves once the dialog
 * is gone, however it was dismissed.
 */
export const openAlertDialog = async (
  options: AlertDialogOptions
): Promise<void> => {
  await enqueue({
    ...options,
    confirmKind: "primary",
    showCancelButton: false,
  });
};

type DialogQueue = {
  next: ComputedRef<QueuedDialog | undefined>;
  take: () => QueuedDialog | undefined;
  registerHost: () => void;
  unregisterHost: () => void;
};

/**
 * Host-facing view of the queue. Only `ConfirmDialogHost` should use this — it
 * takes one request at a time and resolves it itself, so a dialog is never
 * settled by whatever happens to be at the head of the queue at that moment.
 */
export const useDialogQueue = (): DialogQueue => ({
  next: computed(() => queue.value[0]),

  take: (): QueuedDialog | undefined => {
    const [request, ...rest] = queue.value;
    queue.value = rest;

    return request;
  },

  registerHost: (): void => {
    mountedHosts += 1;
  },

  unregisterHost: (): void => {
    mountedHosts -= 1;
    if (mountedHosts > 0) return;

    // Nothing can settle these any more — release the callers rather than
    // leaving their promises pending forever.
    const pending = queue.value;
    queue.value = [];
    pending.forEach((request) => request.resolve(false));
  },
});
